// ─────────────────────────────────────────────────────────────────────────────
// Strategies guide page
//
// Configuration surface for which strategies appear in Trade Entry dropdowns.
// Two parts:
//   - 18 built-in strategies, each with a toggle for visibility
//   - Custom strategies (user-added, also toggleable)
//
// Also notes the Trading Rules pointer card at the bottom — risk limits
// don't live here anymore, they're configured per-account in Trading Rules.
// ─────────────────────────────────────────────────────────────────────────────

import {
  GuideLayout, H2First, H2, P, UL, LI, UI,
  ScreenshotFrame, Callout,
} from "../GuideLayout";

export function GuideStrategies() {
  return (
    <GuideLayout
      title="Strategies"
      subtitle="Toggle which setups appear in your Trade Entry dropdown, and add your own custom strategies."
      prev={{ slug: "portfolio", label: "Portfolio & Trading Rules" }}
      next={{ slug: "market",    label: "Market" }}
    >
      <H2First>What it's for</H2First>
      <P>
        When you log a trade, you pick a strategy (or setup type) from
        a dropdown — Pullback, VWAP Bounce, Breakout, and so on. The
        Strategies page is where you control which options actually
        appear in that dropdown.
      </P>
      <P>
        Most traders use a small handful of setups regularly. The
        default list ships with 18 common ones, but you don't need
        them all. Toggle off the strategies you never trade so your
        dropdown stays focused on the setups that matter to you.
      </P>

      <ScreenshotFrame
        src="/guide/strategies-manage.png"
        alt="Manage Strategies page with toggleable built-in list and custom add field"
        caption="Built-in strategies on top, your custom strategies and add field below."
      />

      <H2>Built-in strategies</H2>
      <P>
        The default list ships with 18 setups. The names are
        intentionally common so most traders recognize them:
      </P>
      <UL>
        <LI>Breakout - New High/Low</LI>
        <LI>Pullback to Support/Resistance</LI>
        <LI>Reversal Candle (Pinbar, Engulfing)</LI>
        <LI>Inside Bar Breakout</LI>
        <LI>Bull/Bear Flag or Pennant</LI>
        <LI>Trend Continuation</LI>
        <LI>Moving Average Crossover</LI>
        <LI>EMA Ribbon Alignment</LI>
        <LI>Volume Spike + Price Action</LI>
        <LI>RSI Divergence</LI>
        <LI>Bollinger Band Squeeze</LI>
        <LI>VWAP Reclaim</LI>
        <LI>Scalp - Momentum Ignition</LI>
        <LI>Opening Range Breakout (ORB)</LI>
        <LI>News / Economic Catalyst</LI>
        <LI>Multiple Indicator Confluence</LI>
        <LI>Order Flow / Delta Divergence</LI>
        <LI>Mean Reversion Setup</LI>
      </UL>
      <P>
        Each one has a checkbox. Check it on and the strategy shows
        up in your Trade Entry dropdown. Uncheck it and it disappears.
        Existing trades logged with that strategy stay tagged with it
        — toggling visibility doesn't retroactively change anything.
      </P>

      <Callout kind="info" title="Toggles are per-user, not per-account">
        Your strategy visibility settings apply across every account
        you trade. If you toggle off "RSI Divergence" while on your
        combine, it's also hidden when you log a trade on your funded
        account. This keeps the dropdown consistent — you don't have
        to re-configure it every time you switch accounts.
      </Callout>

      <Callout kind="tip">
        If your Trade Entry dropdown has 18 options, you're scrolling
        more than thinking. Turn off the ones you never trade — you
        can always turn them back on if your edge expands.
      </Callout>

      <H2>Custom strategies</H2>
      <P>
        Below the built-in list, the <UI>Your Custom Strategies</UI>{" "}
        section lets you add setups that aren't in the default list.
        Type a name in the field, click <UI>Add</UI>, and the
        strategy appears below — visible in your Trade Entry
        dropdown alongside the built-ins.
      </P>
      <P>
        Examples of custom strategies worth adding:
      </P>
      <UL>
        <LI>Setups you've named yourself (e.g. "Morning Gap Fill", "Lunch Reversal")</LI>
        <LI>Variations on built-in setups that you treat as distinct (e.g. "ORB - 5min" vs "ORB - 15min")</LI>
        <LI>Discretionary setups that don't map cleanly to any of the built-in names</LI>
      </UL>
      <P>
        Each custom strategy has a <UI>Delete</UI> button next to it.
        Deleting a custom strategy removes it from future dropdowns —
        like the built-in toggles, it doesn't change historical trades.
      </P>

      <Callout kind="info" title="Be specific, but not too granular">
        The Setup Edge Ranking detector in Analytics needs enough
        samples per setup to produce a reliable read. If you split
        every nuance into its own custom strategy, you'll end up with
        many setup types each with a small sample size — and the
        detector won't be able to tell you which actually works. Keep
        custom strategies broad enough that they collect meaningful
        samples over time.
      </Callout>

      <H2>Risk Limits and Trading Rules</H2>
      <P>
        At the bottom of the Strategies page is a card pointing to{" "}
        <UI>Trading Rules</UI>. Daily drawdown, trade caps, blackout
        windows, and news avoidance used to live on this page; they're
        now configured per-account in <UI>Trading Rules</UI>, because
        a combine and a funded account need different rule sets.
      </P>
      <P>
        Click <UI>Open Rules →</UI> on that card to jump straight to
        the rules page. See the{" "}
        <strong>Portfolio & Trading Rules</strong> guide for the full
        rundown of what's configurable there.
      </P>

      <H2>How strategies show up elsewhere</H2>
      <P>
        Once a strategy is enabled (built-in toggled on, or custom
        added), it surfaces in three places:
      </P>
      <UL>
        <LI><strong>Trade Entry dropdown</strong> — the Entry Signal / Strategy field at the Pre-Trade stage</LI>
        <LI><strong>Analytics → By Setup</strong> — the setup performance table breaks down win rate, expectancy, R-multiple, and P&L by strategy</LI>
        <LI><strong>Analytics → Context → Setup Edge Ranking</strong> — the detector that flags which of your setups are net-positive vs net-negative over your trading history</LI>
      </UL>
      <P>
        This is why the Setup Edge Ranking detector needs sample size:
        it can only tell you whether a setup works if you've taken
        enough of them. Toggling off setups you never trade keeps the
        dropdown clean; toggling them off and on rapidly produces
        scattered data that's hard for the detector to read.
      </P>
    </GuideLayout>
  );
}
